import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Vessel } from 'icreate/common/models/vessel';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';

import { AddDutyEngineerComponent } from '@components/add-duty-engineer/add-duty-engineer.component';
import { SetInputAddressDialogComponent } from './set-input-address-dialog/set-input-address-dialog.component';
import { EditCategoriesDialogComponent } from './edit-categories-dialog/edit-categories-dialog.component';
import { DutyEngineerService } from '@service/duty-engineer.service';
import { DutyEngineerCategoryService } from '@service/duty-engineer-category.service';
import { DutyEngineerCategory } from 'icreate/common/models/duty-engineer-category';
import { MessageDialogService } from '@service/message-dialog.service';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { DutyEngineerView, DutyEngineer, MAX_ENGINEER_COUNT } from 'icreate/common/models/duty-engineer';
import { DutyEngineerControlMode } from 'icreate/common/models/duty-engineer-settings';
import { SensorCableTagService } from '@service/sensor-cable-tag/sensor-cable-tag.service';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { DutyEngineerSettingService } from '@service/duty-engineer-settings.service';
import { VesselService } from '@service/vessel.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { OpsSettingsConfirmDialogComponent } from '@src/app/pages/ops-settings/ops-settings-confirmation-dialog/ops-settings-confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MoreOption, MoreOptionAction } from '@components/more-options/more-options-dropdown.component';

@Component({
    selector: 'app-duty-engineer',
    templateUrl: './duty-engineer.component.html',
    styleUrls: ['./duty-engineer.component.css'],
    standalone: false,
})
export class DutyEngineerComponent implements OnInit, OnDestroy {
    @Input() vessel!: Vessel;
    channelCategories: ChannelCategory[] = [];

    readonly CONSOLE_CONTROL = DutyEngineerControlMode.CONSOLE_CONTROL;
    readonly OPS_CONTROL = DutyEngineerControlMode.OPS_CONTROL;
    protected readonly MAX_ENGINEER_COUNT = MAX_ENGINEER_COUNT;

    isLoading = false;
    engineerList: DutyEngineerView[] = [];
    vesselSlots: SensorTagSlot[] = [];
    controlModeList: DutyEngineerControlMode[] = [this.OPS_CONTROL, this.CONSOLE_CONTROL];
    selectedControlMode: DutyEngineerControlMode = this.controlModeList[0];
    settingsId: string | null = null;
    readonly canEditVessel = toSignal(this.vesselService.accessUpdated$, { initialValue: false });

    private destroy$ = new Subject<void>();
    optionVisible: number = null;

    constructor(
        private dialog: MatDialog,
        private dutyEngineerService: DutyEngineerService,
        private dutyEngineerCategoryService: DutyEngineerCategoryService,
        private messageDialogService: MessageDialogService,
        private sensorCableTagService: SensorCableTagService,
        private channelCategoryService: ChannelCategoryService,
        private dutyEngineerSettingsService: DutyEngineerSettingService,
        private vesselService: VesselService,
        private translate: TranslateService
    ) {}

    @HostListener('document:click')
    clickOut() {
        this.engineerList?.forEach(engineer => {
            engineer.showOptionsDialog = false;
        });
    }

    async ngOnInit() {
        this.isLoading = true;
        const settings = await this.dutyEngineerSettingsService.getByVessel(this.vessel.id);
        if (settings) {
            this.settingsId = settings.id ?? null;
            if (this.controlModeList.includes(settings.controlMode)) {
                this.selectedControlMode = settings.controlMode;
            }
        }

        await Promise.all([this.loadEngineerList(), this.loadVesselSlots(), this.loadChannelCategories()]);
        this.isLoading = false;
        this.subscribeToSocketEvents();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscribeToSocketEvents() {
        this.dutyEngineerService
            .dutyEngineerCreated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((record: DutyEngineer) => {
                if (record.settingId !== this.settingsId) return;
                this.engineerList.push(this.toDisplayModel(record));
            });

        this.dutyEngineerService
            .dutyEngineerUpdated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((record: DutyEngineer) => {
                if (record.settingId !== this.settingsId) return;
                const idx = this.engineerList.findIndex(e => e.id === record.id);
                if (idx !== -1) {
                    this.engineerList[idx] = this.toDisplayModel(record);
                }
            });

        this.dutyEngineerService
            .dutyEngineerDeleted$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ settingsId, updatedList }: { settingsId: string; updatedList: DutyEngineer[] }) => {
                if (settingsId !== this.settingsId) return;
                this.engineerList = updatedList.map(r => this.toDisplayModel(r));
            });

        this.dutyEngineerService
            .dutyEngineerReordered$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ settingsId, updatedList }: { settingsId: string; updatedList: DutyEngineer[] }) => {
                if (settingsId !== this.settingsId) return;
                this.engineerList = updatedList.map(r => this.toDisplayModel(r));
                this.messageDialogService.openMessage('duty-engineer.message-dialog.reordered.message');
            });
    }

    get isConsoleControl(): boolean {
        return this.selectedControlMode === this.CONSOLE_CONTROL;
    }

    get isOpsControl(): boolean {
        return this.selectedControlMode === this.OPS_CONTROL;
    }

    getControlModeLabel(controlType: DutyEngineerControlMode): string {
        return controlType === DutyEngineerControlMode.OPS_CONTROL
            ? 'duty-engineer.OPS.control'
            : 'duty-engineer.Console.control';
    }

    async selectControlType(controlType: DutyEngineerControlMode) {
        if (!this.canEditVessel()) return;
        if (controlType === this.selectedControlMode || !this.settingsId) return;
        const settingsId = this.settingsId;

        if (controlType === this.CONSOLE_CONTROL) {
            const dialogRef = this.dialog.open(OpsSettingsConfirmDialogComponent, {
                width: '474px',
                data: {
                    title: 'duty-engineer.category.control.dialog.title',
                    description: 'duty-engineer.category.control.dialog.content',
                    cancelText: 'duty-engineer.category.control.dialog.cancel',
                    confirmText: 'duty-engineer.category.control.dialog.confirm',
                },
            });
            const result = await lastValueFrom(dialogRef.afterClosed());
            if (!result) return;
        } else if (controlType === this.OPS_CONTROL) {
            const dialogRef = this.dialog.open(OpsSettingsConfirmDialogComponent, {
                width: '474px',
                data: {
                    title: 'duty-engineer.category.OPS.dialog.title',
                    description: 'duty-engineer.category.OPS.dialog.content',
                    cancelText: 'duty-engineer.category.OPS.dialog.cancel',
                    confirmText: 'duty-engineer.category.OPS.dialog.confirm',
                },
            });
            const result = await lastValueFrom(dialogRef.afterClosed());
            if (!result) return;
        }

        try {
            this.isLoading = true;
            await this.dutyEngineerSettingsService.update(settingsId, {
                vesselId: this.vessel.id,
                controlMode: controlType,
            });
            this.selectedControlMode = controlType;
            await this.loadEngineerList();
        } catch (error) {
            console.error('Failed to update control mode:', error);
        } finally {
            this.isLoading = false;
        }
    }

    optionsIconClick(index: number) {
        if (!this.canEditVessel()) return;
        const current = this.engineerList[index].showOptionsDialog;
        this.engineerList.forEach(engineer => (engineer.showOptionsDialog = false));
        this.engineerList[index].showOptionsDialog = !current;
    }

    async addEngineer() {
        if (!this.canEditVessel()) return;
        const dialogRef = this.dialog.open(AddDutyEngineerComponent, {
            width: '474px',
            disableClose: true,
            panelClass: 'add-duty-engineer-dialog',
            data: {
                mode: false,
                settingId: this.settingsId,
                controlType: this.selectedControlMode,
                vessel: this.vessel,
                vesselSlots: this.vesselSlots,
                engineerList: this.engineerList,
            },
        });
        const result = await lastValueFrom(dialogRef.afterClosed());
        if (!result) return;

        const payload = this.buildPayload(result, this.engineerList.length + 1);
        try {
            this.isLoading = true;
            await this.dutyEngineerService.create(payload);
            this.messageDialogService.openMessage('duty-engineer.message-dialog.create.message', {
                name: result.name,
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async editEngineer(engineer: DutyEngineerView) {
        if (!this.canEditVessel()) return;
        const dialogRef = this.dialog.open(AddDutyEngineerComponent, {
            width: '474px',
            disableClose: true,
            panelClass: 'add-duty-engineer-dialog',
            data: {
                mode: true,
                engineer: engineer,
                settingId: this.settingsId,
                controlType: this.selectedControlMode,
                vessel: this.vessel,
                vesselSlots: this.vesselSlots,
                engineerList: this.engineerList,
            },
        });
        const result = await lastValueFrom(dialogRef.afterClosed());
        if (!result) return;

        const payload = this.buildPayload(result, engineer.index);
        try {
            this.isLoading = true;
            await this.dutyEngineerService.update(engineer.id, payload);
            this.messageDialogService.openMessage('duty-engineer.message-dialog.update.message', {
                name: result.name,
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async setInputAddress(engineer: DutyEngineerView) {
        if (!this.isConsoleControl || !this.canEditVessel()) return;

        const dialogRef = this.dialog.open(SetInputAddressDialogComponent, {
            width: '474px',
            disableClose: true,
            panelClass: 'add-duty-engineer-dialog',
            data: {
                engineer: engineer,
                vessel: this.vessel,
                vesselSlots: this.vesselSlots,
            },
        });
        const result = await lastValueFrom(dialogRef.afterClosed());
        if (result === null || result === undefined) return;

        const payload = this.buildPayload(
            {
                name: engineer.name,
                abbreviation: engineer.abbreviation,
                inputAddress: result.inputAddress,
                fcuId: result.fcuId,
                fuId: result.fuId,
                slotId: result.slotId,
            },
            engineer.index
        );
        try {
            this.isLoading = true;
            await this.dutyEngineerService.update(engineer.id, payload);
            this.messageDialogService.openMessage('duty-engineer.message-dialog.set-input-address.message', {
                name: engineer.name,
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async deleteEngineer(engineer: DutyEngineerView) {
        if (!this.canEditVessel()) return;
        const title = this.translate.instant('duty-engineer.category.delete.dialog.title', {
            name: engineer.name,
        });
        const dialogRef = this.dialog.open(OpsSettingsConfirmDialogComponent, {
            width: '474px',
            data: {
                title: title,
                description: 'duty-engineer.category.delete.dialog.content',
                cancelText: 'duty-engineer.category.delete.dialog.cancel',
                confirmText: 'duty-engineer.category.delete.dialog.confirm',
                isDelete: true,
            },
        });
        const result = await lastValueFrom(dialogRef.afterClosed());
        if (!result) return;

        try {
            this.isLoading = true;
            await this.dutyEngineerService.deleteEngineer(engineer.id);
            this.messageDialogService.openMessage('duty-engineer.message-dialog.delete.message', {
                name: engineer.name,
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async editCategories() {
        if (!this.canEditVessel()) return;
        const dialogRef = this.dialog.open(EditCategoriesDialogComponent, {
            width: '1080px',
            disableClose: true,
            data: {
                engineerList: this.engineerList,
                channelCategories: this.channelCategories,
                vesselId: this.vessel.id,
            },
        });
        const result = await lastValueFrom(dialogRef.afterClosed());
        if (!result) return;

        const recordsToSave: DutyEngineerCategory[] = [];
        for (const engineerId of Object.keys(result)) {
            const categories = result[engineerId];
            for (const categoryId of Object.keys(categories)) {
                recordsToSave.push({
                    dutyEngineerId: engineerId,
                    categoryId: categoryId,
                    isChecked: categories[categoryId],
                });
            }
        }

        if (recordsToSave.length > 0) {
            try {
                this.isLoading = true;
                await this.dutyEngineerCategoryService.bulkCreateOrUpdate(recordsToSave);
                this.messageDialogService.openMessage('duty-engineer.message-dialog.set-category.message');
            } catch (error) {
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        }
    }

    private async loadEngineerList() {
        const records = await this.dutyEngineerService.getList(this.vessel.id);
        this.engineerList = records.map(r => this.toDisplayModel(r));
    }

    private async loadChannelCategories() {
        this.channelCategories = await this.channelCategoryService.getList(this.vessel.id);
    }

    private async loadVesselSlots() {
        const slots: any[] = await this.sensorCableTagService.getVesselSlots(this.vessel.id);
        if (slots?.length > 0) {
            this.vesselSlots = slots.map(slot => ({
                no: slot.no,
                slotId: slot.id,
                ioType: slot.ioType,
                fcuNum: slot.FcuUnit?.index,
                fcuId: slot.FcuUnit?.id,
                fuNum: slot.FuUnit?.index,
                fuId: slot.FuUnit?.id,
                terminal: slot.terminal,
                vesselId: this.vessel.id,
            }));
        }
    }

    private buildPayload(
        result: {
            name: string;
            abbreviation: string;
            inputAddress: string;
            fcuId?: string | null;
            fuId?: string | null;
            slotId?: string | null;
        },
        index: number
    ): DutyEngineer {
        const parts = result.inputAddress.split(', ');
        const terminalPart = parts[3] ?? '';
        const terminalValue = terminalPart.replace('Terminal ', '');

        return {
            settingId: this.settingsId ?? '',
            name: result.name,
            abbreviation: result.abbreviation,
            index: index,
            terminal: terminalValue ? parseInt(terminalValue) || null : null,
            fcuId: result.fcuId ?? null,
            fuId: result.fuId ?? null,
            slotId: result.slotId ?? null,
        };
    }

    private toDisplayModel(record: DutyEngineer): DutyEngineerView {
        const matchedSlot = record.slotId ? this.vesselSlots.find(s => s.slotId === record.slotId) : null;
        const fcuIndex = record.FcuUnit?.index ?? matchedSlot?.fcuNum;
        const fuIndex = record.FuUnit?.index ?? matchedSlot?.fuNum;
        const slotNo = record.Slot?.no ?? matchedSlot?.no;

        const fcuPart = fcuIndex != null ? `FCU${fcuIndex}` : '';
        const fuPart = fuIndex != null ? `FU${fuIndex}` : fcuIndex != null ? 'No selection' : '';
        const slotPart = slotNo != null ? `Slot ${String(slotNo).padStart(2, '0')}` : '';
        const terminalPart = record.terminal != null ? `Terminal ${String(record.terminal).padStart(2, '0')}` : '';

        const addressParts = [fcuPart, fuPart, slotPart, terminalPart].filter(p => p);
        return {
            id: record.id ?? '',
            name: record.name,
            abbreviation: record.abbreviation,
            inputAddress: addressParts.length ? addressParts.join(', ') : '',
            showOptionsDialog: false,
            index: record.index,
        };
    }

    async graphDrop(event: CdkDragDrop<DutyEngineer[]>) {
        if (event.previousIndex === event.currentIndex) return;

        const previousOrder = [...this.engineerList];
        moveItemInArray(this.engineerList, event.previousIndex, event.currentIndex);

        const orderedIds = this.engineerList.map(eng => eng.id);
        try {
            await this.dutyEngineerService.reorderList(this.settingsId, orderedIds);
        } catch (error) {
            console.error(error);
            this.engineerList = previousOrder;
        }
    }

    get moreOptions(): MoreOption[] {
        return [
            {
                action: MoreOptionAction.EDIT,
                string: 'dlg.edit',
                disabled: !this.canEditVessel(),
            },
            {
                action: MoreOptionAction.DELETE,
                string: 'dlg.delete',
                disabled: !this.canEditVessel(),
            },
        ];
    }

    onMoreOptionSelected(action: MoreOptionAction, engineer): void {
        switch (action) {
            case MoreOptionAction.EDIT:
                this.editEngineer(engineer);
                break;

            case MoreOptionAction.DELETE:
                this.deleteEngineer(engineer);
                break;
        }
        this.optionVisible = null;
    }

    openOptions(index): void {
        if (this.optionVisible === index) {
            this.optionVisible = null;
            return;
        }
        this.optionVisible = index;
    }
}
